/**
 * Entities Store Actions
 * Type-safe actions using HomeService and DTOs
 *
 * ✅ NOW USES HomeService - No more axios in actions!
 * - Service layer handles all API calls
 * - Actions just orchestrate normalization and commits
 * - Type-safe with TypeScript
 */

import { ActionContext } from 'vuex';
import { HomeService } from '@/services/HomeService';
import { GatewayDTO } from '@/models/dtos';
import { normalizeHomeDTOs, normalizeRegistryDTOs, mergeNormalizedData } from './normalizers';
import { EntitiesState, NormalizedData } from './types';
import { GatewayService } from '@/services/GatewayService';

const apiPrefix = process.env.VUE_APP_API_PREFIX;

// Type for action context with root getters
type ActionCtx = ActionContext<EntitiesState, any> & {
  rootGetters: {
    token: string;
    [key: string]: any;
  };
};

export default {
  /**
   * ✅ Load and normalize homes data using HomeService
   * No more axios here - service layer handles it!
   */
  async loadHomes({ commit }: ActionCtx, userUid: string): Promise<NormalizedData> {
    try {
      // ✅ Use HomeService - returns validated DTOs
      const homeDTOs = await HomeService.getHomesWithRooms(userUid);

      // ✅ Normalize DTOs to store entities
      const normalized = normalizeHomeDTOs(homeDTOs);

      // Merge into store
      commit('mergeHomes', normalized.homes);
      commit('mergeRooms', normalized.rooms);
      commit('mergeDevices', normalized.devices);
      commit('mergeGateways', normalized.gateways);
      commit('updateRelationships', normalized.relationships);
      commit('updateCacheTimestamp');

      return normalized;
    } catch (error) {
      console.error('[Entities] Error loading homes:', error);
      throw error;
    }
  },

  /**
   * ✅ Load and normalize registries data
   * TODO: Create GatewayService to replace axios here
   */
  async loadRegistries({ commit }: ActionCtx, userUid: string): Promise<Partial<NormalizedData>> {
    try {
      const registriesArray = await GatewayService.getRegistries(userUid);

      // ✅ Convert to DTOs and normalize
      const gatewayDTOs = GatewayDTO.fromApiResponseArray(registriesArray);
      const normalized = normalizeRegistryDTOs(gatewayDTOs);

      // Merge into store
      commit('mergeDevices', normalized.devices);
      commit('mergeGateways', normalized.gateways);
      commit('updateRelationships', normalized.relationships);
      commit('updateCacheTimestamp');

      return normalized;
    } catch (error) {
      console.error('[Entities] Error loading registries:', error);
      throw error;
    }
  },

  /**
   * ✅ Load all data (homes + registries) in parallel
   * Type-safe with proper return type
   */
  async loadAllData({ dispatch, commit }: ActionCtx, userUid: string): Promise<NormalizedData> {
    try {
      // Load both in parallel
      const [homesNormalized, registriesNormalized] = await Promise.all([
        dispatch('loadHomes', userUid),
        dispatch('loadRegistries', userUid),
      ]);

      const merged = mergeNormalizedData(homesNormalized, registriesNormalized);

      // Reconcile the store with this full backend snapshot. loadHomes/loadRegistries
      // above only merge new data in, so entities deleted on the backend (e.g. from
      // another device) would otherwise linger in the store/localStorage cache forever.
      // This replace ensures every refresh fully syncs to what the backend just returned.
      commit('replaceEntities', merged);

      return merged;
    } catch (error) {
      console.error('[Entities] Error loading all data:', error);
      throw error;
    }
  },

  /**
   * Set photo URL for a home
   */
  setPhotoUrl(
    { commit }: ActionCtx,
    payload: { homeUid: string; photoUrl: string }
  ): void {
    commit('setPhotoUrlForHome', payload);
  },

  /**
   * Clear all cached data
   */
  clearCache({ commit }: ActionCtx): void {
    commit('clearCache');
  },

  /**
   * Restore state from localStorage (used by persistence plugin)
   */
  restoreFromCache({ commit }: ActionCtx, cachedState: Partial<EntitiesState>): void {
    if (cachedState) {
      commit('setNormalizedData', cachedState);
    }
  },
};
